/**
 * Client image gallery for the canonical PDP (`/p/[id]`). Replaces the static
 * server-rendered main image + "More views" thumbnails so the shopper can:
 *   - click a thumbnail to switch the main image, and
 *   - click the main image to open it full-screen (shared GalleryLightbox),
 *     paging through every view with the lightbox arrows / keyboard.
 *
 * The main image keeps `priority` + `fetchPriority="high"` so it stays the LCP
 * element (client components still SSR, so the <img> + preload ship in the
 * initial HTML).
 */

"use client";

import Image from "next/image";
import dynamic from "next/dynamic";
import { useCallback, useState } from "react";
import { Expand } from "lucide-react";
import { Skeleton } from "@/components/ui/skeleton";

const GalleryLightbox = dynamic(
  () =>
    import("@/app/(main)/gallery/GalleryLightbox").then((m) => ({
      default: m.GalleryLightbox,
    })),
  {
    ssr: false,
    loading: () => (
      <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/80">
        <Skeleton className="h-[60vh] w-[80vw] rounded-lg" />
      </div>
    ),
  }
);

export interface ProductGalleryImage {
  url: string;
  alt: string;
}

export function ProductGallery({
  images,
  title,
}: {
  images: ProductGalleryImage[];
  title: string;
}) {
  const [active, setActive] = useState(0);
  const [lightboxOpen, setLightboxOpen] = useState(false);
  const total = images.length;
  const safeActive = active < total ? active : 0;
  const current = images[safeActive] ?? images[0];

  const step = useCallback(
    (delta: number) => setActive((i) => (i + delta + total) % total),
    [total]
  );

  if (total === 0 || !current) return null;

  const lightboxItem = {
    id: `pg-${safeActive}`,
    title,
    mediaType: "image" as const,
    imageUrl: current.url,
  };

  return (
    <div className="flex flex-col gap-3">
      <button
        type="button"
        onClick={() => setLightboxOpen(true)}
        aria-label="View image full size"
        aria-haspopup="dialog"
        className="group relative aspect-square w-full cursor-zoom-in overflow-hidden rounded-2xl bg-muted focus-visible:outline-hidden focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2"
      >
        <Image
          // Video-only products pass the brand banner as their single image.
          src={current.url}
          alt={current.alt}
          fill
          priority
          fetchPriority="high"
          sizes="(min-width: 1024px) 50vw, 100vw"
          className="object-cover transition-transform duration-300 group-hover:scale-[1.02]"
        />
        <span
          className="absolute bottom-3 right-3 flex size-9 items-center justify-center rounded-full bg-card/90 text-foreground shadow-sm opacity-0 backdrop-blur-sm transition-opacity duration-200 group-hover:opacity-100 group-focus-visible:opacity-100"
          aria-hidden="true"
        >
          <Expand className="h-4 w-4" />
        </span>
      </button>

      {total > 1 && (
        <figure aria-labelledby="more-views-label">
          <figcaption
            id="more-views-label"
            className="label-overline mb-2 text-muted-foreground"
          >
            More views
          </figcaption>
          <div
            className="grid grid-cols-5 gap-2"
            role="group"
            aria-labelledby="more-views-label"
          >
            {images.map((img, idx) => (
              <button
                key={`${img.url}-${idx}`}
                type="button"
                onClick={() => setActive(idx)}
                aria-label={`Show view ${idx + 1} of ${total}`}
                aria-current={idx === safeActive}
                className={`relative aspect-square overflow-hidden rounded-lg bg-muted transition-all focus-visible:outline-hidden focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-1 ${
                  idx === safeActive
                    ? "ring-2 ring-primary ring-offset-1"
                    : "opacity-75 hover:opacity-100"
                }`}
              >
                <Image
                  src={img.url}
                  alt=""
                  fill
                  sizes="(min-width: 1024px) 10vw, 20vw"
                  loading="lazy"
                  className="object-cover"
                />
              </button>
            ))}
          </div>
        </figure>
      )}

      {lightboxOpen && (
        <GalleryLightbox
          item={lightboxItem}
          index={safeActive}
          total={total}
          open
          onClose={() => setLightboxOpen(false)}
          onPrev={() => step(-1)}
          onNext={() => step(1)}
        />
      )}
    </div>
  );
}
